import React from "react";
import { Offcanvas } from "react-bootstrap";
import type { AuditLogsScopeType } from "../molecules/AuditLogsScopePill";
import "./AuditLogDetailDrawer.css";

export interface AuditLogDetailEntry {
  id: string;
  timestamp: string;
  actor: string;
  actedOn: string;
  permission?: string;
  scope: AuditLogsScopeType;
  scopeId: string;
  resource?: string;
  before: string;
  after: string;
  action?: string;
  targetType?: string;
  details?: Record<string, any> | null;
}

interface AuditLogDetailDrawerProps {
  show: boolean;
  onHide: () => void;
  log: AuditLogDetailEntry | null;
}

const AuditLogDetailDrawer: React.FC<AuditLogDetailDrawerProps> = ({
  show,
  onHide,
  log,
}) => {
  if (!log) return null;

  const sanitize = (value: any): any => {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) {
      return value.map(sanitize);
    }
    if (typeof value !== 'object') {
      return value;
    }
    const result: Record<string, any> = {};
    for (const [key, val] of Object.entries(value)) {
      const lower = key.toLowerCase();
      // Drop technical IDs and similar fields from the view.
      if (lower.includes('id')) {
        continue;
      }
      // For isActive flags, map boolean to a friendly label.
      if (lower === 'isactive') {
        if (val === true) {
          result[key] = 'Enabled';
        } else if (val === false) {
          result[key] = 'Disabled';
        } else {
          result[key] = sanitize(val);
        }
        continue;
      }
      result[key] = sanitize(val);
    }
    return result;
  };

  const stringify = (obj: any): string => {
    if (!obj) return '—';
    try {
      const cleaned = sanitize(obj);
      return JSON.stringify(cleaned, null, 2);
    } catch {
      return String(obj);
    }
  };

  // Derive before/after display based on targetType and details payload.
  let derivedBefore: string = log.before;
  let derivedAfter: string = log.after;

  const { targetType, details } = log;
  if (details && typeof details === 'object') {
    switch (targetType) {
      case 'role': {
        const anyDetails: any = details;
        if (anyDetails.before || anyDetails.after) {
          // For role updates, prefer concise permissions diff when permissions arrays are present.
          const beforeObj: any = anyDetails.before || {};
          const afterObj: any = anyDetails.after || {};

          const beforePerms = Array.isArray(beforeObj.permissions)
            ? beforeObj.permissions.join(', ')
            : undefined;
          const afterPerms = Array.isArray(afterObj.permissions)
            ? afterObj.permissions.join(', ')
            : undefined;

          if (beforePerms !== undefined || afterPerms !== undefined) {
            derivedBefore = beforePerms ?? '—';
            derivedAfter = afterPerms ?? '—';
          } else {
            // Fallback to structured JSON view if permissions array is not present.
            derivedBefore = stringify(anyDetails.before);
            derivedAfter = stringify(anyDetails.after);
          }
        } else {
          // For role creation, show only the role code in After.
          derivedBefore = '—';
          derivedAfter = anyDetails.code || '—';
        }
        break;
      }
      case 'permission': {
        // For permission CRUD, show only the permission code in After.
        const anyDetails: any = details;
        derivedBefore = '—';
        derivedAfter = anyDetails.code || '—';
        break;
      }
      case 'role_permission': {
        // For role_permission changes, show roleCode and permissionCode in After.
        const anyDetails: any = details;
        const roleCode = anyDetails.roleCode || '';
        const permissionCode = anyDetails.permissionCode || '';
        derivedBefore = '—';
        derivedAfter = [roleCode, permissionCode].filter(Boolean).join(' - ') || '—';
        break;
      }
      case 'assignment': {
        const anyDetails: any = details;
        if (anyDetails.before || anyDetails.after) {
          const beforeFlag = anyDetails.before && typeof anyDetails.before === 'object'
            ? (anyDetails.before as any).isActive
            : undefined;
          const afterFlag = anyDetails.after && typeof anyDetails.after === 'object'
            ? (anyDetails.after as any).isActive
            : undefined;

          const mapFlag = (val: any): string => {
            if (val === true) return 'Enabled';
            if (val === false) return 'Disabled';
            return '—';
          };

          derivedBefore = beforeFlag === undefined ? '—' : mapFlag(beforeFlag);
          derivedAfter = afterFlag === undefined ? '—' : mapFlag(afterFlag);
        } else {
          // For initial role assignment, show only the roleCode in After.
          const anyDetails: any = details;
          derivedBefore = '—';
          derivedAfter = anyDetails.roleCode || '—';
        }
        break;
      }
      default: {
        // leave derivedBefore/derivedAfter as-is
      }
    }
  }

  const detailRows = [
    { label: "Actor:", value: log.actor },
    { label: "Acted On:", value: log.actedOn },
    { label: "Action:", value: log.action ?? '—' },
    { label: "Target Type:", value: log.targetType ?? '—' },
    { label: "Permission:", value: log.permission ?? "—" },
    { label: "Scope:", value: log.scope },
    { label: "Scope ID:", value: log.scopeId },
    { label: "Resource:", value: log.resource ?? "—" },
    { label: "Before:", value: derivedBefore, multiline: true },
    { label: "After:", value: derivedAfter, multiline: true },
  ];

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="audit-log-detail-drawer"
      style={{ width: 640, maxWidth: "100%" }}
    >
      <div className="audit-log-detail-drawer-header">
        <h2 className="audit-log-detail-drawer-title">Audit Log Detail</h2>
        <button
          type="button"
          className="audit-log-detail-drawer-close btn-close"
          onClick={onHide}
          aria-label="Close"
        />
      </div>
      <Offcanvas.Body className="audit-log-detail-drawer-body">
        <div className="audit-log-detail-card">
          {detailRows.map((row) => (
            <div key={row.label} className="audit-log-detail-row">
              <span className="audit-log-detail-label">{row.label}</span>
              <span
                className="audit-log-detail-value"
                style={row.multiline ? { whiteSpace: 'pre-wrap', fontFamily: 'monospace' } : undefined}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AuditLogDetailDrawer;
